import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createInvoice, updateInvoiceStatus } from '../api/invoices.js';
import { saveInvoiceToDropbox } from '../api/dropbox.js';
import { createSaveSendInvoice } from '../lib/createSaveSendInvoice.js';
import { fetchAmbassadorData } from '../api/contracts.js';
import { usePacksByLead, useCreatePack } from '../api/packs.js';
import { useLeadAccounts } from '../api/leads.js';
import { queryKeys } from '../lib/queryKeys';
import { useToast } from '../contexts/ToastContext';
import { placementToBasis, paymentBasisLabel } from '../lib/paymentBasis';
import { APPS, appBadge } from '../constants/apps.js';
import { formatPaymentMethod } from '../lib/paymentMethod';
import MonthPicker from './MonthPicker';
import { NumberStepper } from './NumberStepper.jsx';
import InvoiceExtrasEditor, {
  emptyExtras, toApiExtras, extrasTotalCents, extrasIssues, centsOf, MONEY_RE,
} from './InvoiceExtrasEditor.jsx';
import DocForm from './DocForm.jsx';
import { packDisplayName } from '../lib/packName.js';

// Phase 65 D-08: default selected month = current calendar month.
function defaultMonth() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * @param {object} props
 * @param {object} props.contract - Ambassador contract row (must include leadId).
 * @param {Function} props.onClose - Called when modal is dismissed.
 * @param {'bonus'|'payoneer'|'paypal'|null} [props.defaultPaymentType=null]
 *   Pre-fills the paymentType field. Pass 'bonus' from the «+ Бонус» flow
 *   (plan 07). Defaults to null — no pre-fill (back-compat).
 * @param {'inline'|'overlay'} [props.host='overlay'] - DocForm host: 'inline' from the lead card's
 *   «Инвойсы» slot (ContractCard); the default keeps the /ambassadors mount an overlay.
 */
export default function CreateInvoiceModal({ contract, onClose, defaultPaymentType = null, host = 'overlay' }) {
  // Phase 65 D-07–D-09: single month selector replaces 3 native date inputs.
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  // D3 (owner ruling 3, LEDGER 319): the BASE, not the total — the server derives fee = base + Σ.
  const [baseFee, setBaseFee] = useState(
    contract?.ambassadorFee !== undefined && contract?.ambassadorFee !== null
      ? String(contract.ambassadorFee)
      : ''
  );
  // Unlike the draft editor, an empty form IS the truth on mount, so there is no `null` phase here.
  const [extras, setExtras] = useState(emptyExtras);
  // Phase 93 D-93-03: paymentType pre-filled from defaultPaymentType prop.
  const [paymentType, setPaymentType] = useState(defaultPaymentType);
  // Phase 100: manual video count, internal stats only. Optional ('' = NULL).
  const [videoCount, setVideoCount] = useState('');
  // P1: manual bonus count, internal stats only. Optional ('' = omit).
  const [bonusVideoCount, setBonusVideoCount] = useState('');
  // P2: payment basis — derived from contract placementType (read-only).
  const paymentBasis = placementToBasis(contract?.placementType);

  // Phase 101: every invoice binds to a Pack (no separate per-account/bonus invoice).
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [createPackOpen, setCreatePackOpen] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  // PACK-APP-REQ: the new pack's application — required, no default.
  const [newPackApp, setNewPackApp] = useState('');

  // Task 11: donor select for onetime invoices.
  const isOnetime = contract?.placementType === 'onetime';
  const [selectedDonorId, setSelectedDonorId] = useState(null);

  const qc = useQueryClient();
  const { addToast } = useToast();

  // Phase 101: packs for this lead.
  const { data: packs = [], isLoading: packsLoading } = usePacksByLead(contract?.leadId);
  const createPackMut = useCreatePack();
  const leadAccountsQuery = useLeadAccounts(createPackOpen ? contract?.leadId : null);
  const unassignedAccounts = useMemo(
    () => (leadAccountsQuery.data?.accounts ?? []).filter((a) => !a.packId),
    [leadAccountsQuery.data],
  );

  // Task 11: donor accounts (blogger-purpose) for onetime contracts.
  const donorAccountsQuery = useLeadAccounts(isOnetime ? contract?.leadId : null);
  const donorAccounts = useMemo(
    () => (donorAccountsQuery.data?.accounts ?? []).filter((a) => a.accountPurpose === 'blogger'),
    [donorAccountsQuery.data],
  );
  const packLabel = packDisplayName;
  // INVOICE-APP: the picked pack's application, shown under the select («без приложения» if none).
  const selectedPack = packs.find((p) => p.id === selectedPackId);
  const packApp = selectedPack ? appBadge(selectedPack.application) : null;

  // Phase 65 D-26: fetch ambassador data so the modal can render the current
  // effective payment method+address as a read-only line and pre-fill the
  // override radio/input. Same fetch pattern as AmbassadorDataModal.
  const { data: ambassador } = useQuery({
    queryKey: ['contract-detail', contract?.id],
    queryFn: () => fetchAmbassadorData(contract.id),
    enabled: !!contract?.id,
    staleTime: 30_000,
  });

  // fetchAmbassadorData → GET /contracts/:id возвращает контракт с вложенным
  // ambassadorData (как читает AmbassadorDataModal: data?.ambassadorData).
  // Платёжные реквизиты живут там, а не на верхнем уровне.
  const amb = ambassador?.ambassadorData;

  // Phase 65 D-26 toggle state (default OFF — backend resolves contract defaults).
  const defaultPm = contract?.paymentMethod || amb?.paymentMethod || '';
  const defaultPa = amb?.paymentAddress || '';
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overridePaymentMethod, setOverridePaymentMethod] = useState(defaultPm);
  const [overridePaymentAddress, setOverridePaymentAddress] = useState(defaultPa);

  // Re-sync override defaults once ambassador data arrives — only if the user
  // hasn't typed anything yet. This keeps the radio/input pre-filled with the
  // current effective values without overwriting in-flight edits.
  useEffect(() => {
    if (!amb) return;
    setOverridePaymentMethod((prev) => prev || contract?.paymentMethod || amb.paymentMethod || '');
    setOverridePaymentAddress((prev) => prev || amb.paymentAddress || '');
  }, [amb, contract?.paymentMethod]);

  // Build the create payload from current form state. Shared by the plain «Создать»
  // mutation and the P10 combined «save to Dropbox + send to payment» flow.
  const buildCreatePayload = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const periodStart = new Date(Date.UTC(y, m - 1, 1)).toISOString().slice(0, 10);
    const periodEnd = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    const issueDate = new Date().toISOString().slice(0, 10);

    // Phase 65 D-26/D-27: include override fields only when toggle is active.
    // Backend resolves defaults from contract.paymentMethod + ambassador.paymentAddress
    // when both fields are absent.
    // D3: baseFee + extraPayments, never a `fee` key (both = BASE_AND_FEE_CONFLICT). Amounts go as the
    // trimmed input STRINGS: Number('1.10') is 1.1, the string survives byte-for-byte to toMoneyString.
    const payload = {
      issueDate, periodStart, periodEnd,
      baseFee: baseFee.trim(),
      extraPayments: toApiExtras(extras),
    };
    if (overrideOpen && overridePaymentMethod && overridePaymentAddress.trim()) {
      payload.paymentMethod = overridePaymentMethod;       // lowercase wire value
      payload.paymentAddress = overridePaymentAddress.trim();
    }
    // Phase 101 / Task 11: onetime → donor accountId; recurring → Pack.
    if (isOnetime) {
      payload.accountId = selectedDonorId;
    } else if (selectedPackId) {
      payload.packId = selectedPackId;
    }
    if (paymentType) payload.paymentType = paymentType;
    payload.paymentBasis = paymentBasis; // P2 — always set (ambassador|ad_hoc)
    // Phase 100: include videoCount when entered. Empty string → omit (NULL).
    if (videoCount !== '' && videoCount !== null) {
      const n = Number(videoCount);
      if (Number.isInteger(n) && n >= 0) payload.videoCount = n;
    }
    // P1: include bonusVideoCount when entered. Empty string → omit.
    if (bonusVideoCount !== '' && bonusVideoCount !== null) {
      const n = Number(bonusVideoCount);
      if (Number.isInteger(n) && n >= 0) payload.bonusVideoCount = n;
    }
    return payload;
  };

  const mut = useMutation({
    mutationFn: () => createInvoice(contract.id, buildCreatePayload()),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(contract.id) });
      qc.invalidateQueries({ queryKey: ['contract-detail', contract.id] });
      addToast('Создано', `Инвойс ${created.invoiceNo} создан`, 'success');
      onClose();
    },
    onError: (err) => {
      // D9: api/client.js rejects with a plain Error — .message/.status/.data, no .response.
      addToast('Ошибка', err?.data?.error || err?.message || 'Не удалось создать инвойс', 'error');
    },
  });

  // P10: one click → create invoice → save to Dropbox → send to payment (in_payment).
  // The orchestrator stops at the first failed step and reports which one, so a
  // partial run leaves a visible draft and the user is told exactly what happened.
  const combinedMut = useMutation({
    mutationFn: () => createSaveSendInvoice({
      contractId: contract.id,
      payload: buildCreatePayload(),
      createInvoice,
      saveInvoiceToDropbox,
      updateInvoiceStatus,
    }),
    onSuccess: (result) => {
      if (result.created) {
        qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(contract.id) });
        qc.invalidateQueries({ queryKey: ['contract-detail', contract.id] });
      }
      if (result.ok) {
        addToast('Готово', `Инвойс ${result.created.invoiceNo} создан, сохранён в Dropbox и отправлен на оплату`, 'success');
        onClose();
      } else if (result.step === 'create') {
        addToast('Ошибка', result.error?.data?.error || result.error?.message || 'Не удалось создать инвойс', 'error');
      } else if (result.step === 'dropbox') {
        addToast('Частично', `Инвойс ${result.created.invoiceNo} создан (черновик), но НЕ сохранён в Dropbox. Сохраните вручную из списка.`, 'warning');
        onClose();
      } else if (result.step === 'status') {
        addToast('Частично', `Инвойс ${result.created.invoiceNo} сохранён в Dropbox, но НЕ отправлен на оплату (остался черновиком).`, 'warning');
        onClose();
      }
    },
    onError: () => {
      addToast('Ошибка', 'Не удалось выполнить операцию', 'error');
    },
  });

  // D-27 client validation: when override is open, both radio AND address
  // must be filled. When closed, no override fields are required.
  const overrideValid =
    !overrideOpen ||
    (!!overridePaymentMethod && overridePaymentAddress.trim().length > 0);

  const busy = mut.isPending || combinedMut.isPending;
  const videoCountValid =
    videoCount !== '' &&
    Number.isInteger(Number(videoCount)) &&
    Number(videoCount) >= 1;

  // D4: derived, never typed — base + Σ in integer cents, mirroring moneyCents. Renders «—» while the
  // base fails MONEY_RE or any amount/note issue stands: never a total the server would refuse.
  const invoiceTotal = (!MONEY_RE.test(baseFee.trim()) || extrasIssues(extras).length > 0)
    ? '—'
    : `USD ${((centsOf(baseFee.trim()) + extrasTotalCents(extras)) / 100).toFixed(2)}`;

  // D4: the mirror makes INVALID_BASE_FEE / INVALID_EXTRA_AMOUNT / EXTRA_NOTE_REQUIRED /
  // FEE_OUT_OF_RANGE unreachable by typing. 1e10 cents = the server's MAX_CENTS.
  const canSubmit =
    !!selectedMonth &&
    MONEY_RE.test(baseFee.trim()) &&
    extrasIssues(extras).length === 0 &&
    centsOf(baseFee.trim()) + extrasTotalCents(extras) < 1e10 &&
    (isOnetime ? !!selectedDonorId : !!selectedPackId) &&
    (isOnetime || videoCountValid) &&
    overrideValid &&
    !busy;

  // Phase 65 D-26 read-only display: "{providerLabel} account: {address}"
  // formatPaymentMethod handles the "—" fallback when neither contract nor
  // ambassador has a method set.
  const effective = formatPaymentMethod(contract, amb);

  const PROVIDER_OPTIONS = [
    { v: 'paypal',   label: 'PayPal' },
    { v: 'payoneer', label: 'Payoneer' },
    { v: 'crypto',   label: 'Crypto' },
    { v: 'garna',    label: 'Garna' },
  ];

  return (
    <DocForm
      title="Новый инвойс"
      meta={`Тип оплаты: ${paymentBasisLabel(paymentBasis)}`}
      onClose={onClose}
      closeDisabled={busy}
      host={host}
      actions={(
        <>
          <button
            type="button"
            className="documents-action-btn"
            onClick={onClose}
            disabled={busy}
          >
            Отмена
          </button>
          <button
            type="button"
            className="documents-action-btn"
            onClick={() => mut.mutate()}
            disabled={!canSubmit}
          >
            {mut.isPending ? 'Создание…' : 'Создать'}
          </button>
          {/* P10: one-click create → save to Dropbox → send to payment. Same
              validity gate as «Создать». */}
          <button
            type="button"
            className="documents-action-btn documents-action-btn--primary"
            onClick={() => combinedMut.mutate()}
            disabled={!canSubmit}
            title="Создать инвойс, сохранить его в Dropbox и сразу перевести в статус «На оплате»"
          >
            {combinedMut.isPending ? 'Обработка…' : 'Сохранить на Dropbox и отправить на оплату'}
          </button>
        </>
      )}
    >
      <div className="doc-form__grid">
        {/* Phase 101 / Task 11: onetime → donor select; recurring → pack block. */}
        {isOnetime ? (
          <div className="contract-inline-form__field doc-form__span2">
            <label>Донор (личный аккаунт)</label>
            <select
              value={selectedDonorId ?? ''}
              onChange={(e) => setSelectedDonorId(e.target.value || null)}
            >
              <option value="" disabled>— выберите аккаунт —</option>
              {donorAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.platform}: {a.handle}{a.isPrimary ? ' ★' : ''}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="contract-inline-form__field doc-form__span2">
            <label>Пачка</label>
            {packsLoading ? (
              <p className="doc-form__muted" style={{ fontSize: 13 }}>Загрузка пачек…</p>
            ) : packs.length === 0 && !createPackOpen ? (
              <div>
                <p className="doc-form__muted">
                  У этого блогера нет пачек.
                </p>
                <button
                  type="button"
                  className="documents-action-btn"
                  onClick={() => setCreatePackOpen(true)}
                >
                  Создать пачку
                </button>
              </div>
            ) : createPackOpen ? (
              <div className="pack-create-panel">
                <p className="pack-create-panel__hint">
                  Выбери аккаунты, которые войдут в эту пачку. Один аккаунт может состоять только в одной пачке.
                </p>
                {leadAccountsQuery.isLoading ? (
                  <p className="doc-form__muted">Загрузка аккаунтов…</p>
                ) : unassignedAccounts.length === 0 ? (
                  <p className="doc-form__muted">
                    Все аккаунты лида уже привязаны к пачкам.
                  </p>
                ) : (
                  <ul className="pack-create-panel__list">
                    {unassignedAccounts.map((a) => (
                      <li key={a.id}>
                        <label className="pack-create-panel__row">
                          <input
                            type="checkbox"
                            checked={selectedAccountIds.includes(a.id)}
                            onChange={(e) => {
                              setSelectedAccountIds((prev) => (
                                e.target.checked
                                  ? [...prev, a.id]
                                  : prev.filter((id) => id !== a.id)
                              ));
                            }}
                          />
                          <span className="pack-create-panel__platform">{a.platform}</span>
                          <span className="pack-create-panel__handle">@{a.handle}</span>
                          {a.isPrimary && <span className="pack-create-panel__primary">primary</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="pack-create-panel__hint">Приложение пачки</p>
                <select
                  className="pack-create-panel__app"
                  aria-label="Приложение"
                  value={newPackApp}
                  onChange={(e) => setNewPackApp(e.target.value)}
                >
                  <option value="">— Выберите приложение —</option>
                  {APPS.map((a) => <option key={a.value} value={a.value}>{a.value}</option>)}
                </select>
                <div className="pack-create-panel__actions">
                  <button
                    type="button"
                    className="documents-action-btn"
                    onClick={() => { setCreatePackOpen(false); setSelectedAccountIds([]); setNewPackApp(''); }}
                    disabled={createPackMut.isPending}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="documents-action-btn documents-action-btn--primary"
                    disabled={createPackMut.isPending || selectedAccountIds.length === 0 || !newPackApp}
                    onClick={() => {
                      createPackMut.mutate(
                        { leadId: contract.leadId, accountIds: selectedAccountIds, application: newPackApp },
                        {
                          onSuccess: (data) => {
                            const newId = data?.pack?.id ?? data?.id;
                            if (newId) setSelectedPackId(newId);
                            addToast('Готово', `Пачка создана (${selectedAccountIds.length} акк.)`, 'success');
                            setCreatePackOpen(false);
                            setSelectedAccountIds([]);
                            setNewPackApp('');
                          },
                          onError: (err) => {
                            const code = err?.data?.error;
                            const msg = code === 'account_already_in_pack'
                              ? 'Один из аккаунтов уже в другой пачке — обнови страницу.'
                              : 'Не удалось создать пачку';
                            addToast('Ошибка', msg, 'error');
                          },
                        },
                      );
                    }}
                  >
                    {createPackMut.isPending ? 'Создание…' : `Создать пачку (${selectedAccountIds.length})`}
                  </button>
                </div>
              </div>
            ) : (
              <div className="pack-select-row">
                <select
                  value={selectedPackId ?? ''}
                  onChange={(e) => setSelectedPackId(e.target.value || null)}
                >
                  <option value="">— Выберите пачку —</option>
                  {packs.map((p) => (
                    <option key={p.id} value={p.id}>{packLabel(p)}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="pack-select-row__add"
                  onClick={() => setCreatePackOpen(true)}
                  title="Создать ещё одну пачку"
                >
                  + пачка
                </button>
              </div>
            )}
            {/* Under the row, not in it: the select does not shrink below its longest option. */}
            {packApp && !createPackOpen && <div className="pack-select-app"><span className={packApp.className}>{packApp.label}</span></div>}
          </div>
        )}

        {/* Task 3: видео — счётчик, не сумма. videoCount required >= 1 (Task 1). */}
        <div className="contract-inline-form__field">
          <label htmlFor={`video-count-${contract.id}`} title="Количество видео (шт.)">Количество видео (шт.)</label>
          <NumberStepper id={`video-count-${contract.id}`} value={videoCount} onChange={setVideoCount} min={1} ariaLabel="количество видео" />
        </div>

        {/* Task 3: бонусы — это КОЛИЧЕСТВО бонусных видео, не денежная сумма. */}
        <div className="contract-inline-form__field" title="количество, не сумма">
          <label htmlFor={`bonus-video-count-${contract.id}`}>Бонусные видео (шт.)</label>
          <NumberStepper id={`bonus-video-count-${contract.id}`} value={bonusVideoCount} onChange={setBonusVideoCount} min={0} ariaLabel="бонусные видео" />
        </div>

        <div className="contract-inline-form__field">
          <label htmlFor={`month-picker-${contract.id}`}>Месяц</label>
          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
        </div>

        <div className="contract-inline-form__field">
          <label htmlFor={`base-fee-${contract.id}`}>Базовая сумма (USD)</label>
          <input
            id={`base-fee-${contract.id}`}
            type="number"
            step="0.01"
            min="0"
            value={baseFee}
            onChange={(e) => setBaseFee(e.target.value)}
          />
          {baseFee.trim() !== '' && !MONEY_RE.test(baseFee.trim()) && (
            <p className="doc-form__error">База — число, максимум 2 знака после точки</p>
          )}
        </div>
      </div>

      {/* D7a: P-E3 inserts its Q19 VPN hint immediately ABOVE this mount — it stays on its own
          line with exactly `value` and `onChange`, and gains no third prop. */}
      {contract?.postingViaVpn && (
        <p className="doc-form__hint">По договору включён VPN — добавьте допвыплату VPN/Proxy</p>
      )}
      <InvoiceExtrasEditor value={extras} onChange={setExtras} />

      {/* F6: всегда явно показываем, на какую связку уйдёт платёж — рядом с итогом. */}
      <div className="doc-form__strip">
        <span className="doc-form__strip-total">Итого по инвойсу: {invoiceTotal}</span>
        <span
          className="doc-form__strip-pay"
          title={effective.addressDetail
            ? `${effective.providerLabel} — ${effective.addressDetail}`
            : `${effective.providerLabel} — не указан`}
        >
          Платёж уйдёт на: {effective.addressDetail
            ? `${effective.providerLabel} — ${effective.addressDetail}`
            : `${effective.providerLabel} — не указан`}
        </span>
      </div>

      {/* Phase 65 D-26: override toggle. Default OFF → server resolves defaults. */}
      <div className="doc-form__check">
        <label className="contract-inline-form__checkbox">
          <input
            type="checkbox"
            checked={overrideOpen}
            onChange={(e) => setOverrideOpen(e.target.checked)}
          />
          Изменить платёжные данные блогера
        </label>
        <span className="doc-form__hint">
          Изменение сохранится у блогера и применится к будущим инвойсам.
        </span>
      </div>

      {overrideOpen && (
        <div className="doc-form__grid">
          <div className="contract-inline-form__field">
            <label>Способ оплаты</label>
            <div className="doc-form__radios">
              {PROVIDER_OPTIONS.map((opt) => (
                <label key={opt.v}>
                  <input
                    type="radio"
                    name={`override-pm-${contract.id}`}
                    value={opt.v}
                    checked={overridePaymentMethod === opt.v}
                    onChange={() => setOverridePaymentMethod(opt.v)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className="contract-inline-form__field doc-form__span2">
            <label htmlFor={`override-pa-${contract.id}`}>Email или кошелёк</label>
            <input
              id={`override-pa-${contract.id}`}
              type="text"
              value={overridePaymentAddress}
              onChange={(e) => setOverridePaymentAddress(e.target.value)}
              placeholder="например, nikita@example.com"
            />
          </div>
        </div>
      )}
    </DocForm>
  );
}
